import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Heart, ArrowLeftRight, ShoppingBag, Minus, Plus, Bell, Check } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { useShop } from "@/context/ShopContext";
import { formatPrice, SIZES, PAYMENT_METHODS } from "@/lib/constants";
import StarRating from "@/components/StarRating";
import Reveal from "@/components/Reveal";

const NOTE_LABELS = [
  { key: "tete", label: "Notes de tête", desc: "La première impression, vive et éclatante" },
  { key: "coeur", label: "Notes de cœur", desc: "L'âme du parfum, qui se révèle après quelques minutes" },
  { key: "fond", label: "Notes de fond", desc: "Le sillage profond, qui dure des heures" },
];

const THIRTY_DAYS = 30 * 24 * 3600 * 1000;

const ProductDetail = () => {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(1);
  const [review, setReview] = useState({ name: "", rating: 5, comment: "" });
  const [notifyContact, setNotifyContact] = useState("");
  const [notifyDone, setNotifyDone] = useState(false);
  const { addToCart, favorites, toggleFavorite, compare, toggleCompare } = useShop();

  const load = () => {
    api
      .get(`/perfumes/${id}`)
      .then((r) => {
        setP(r.data);
        const s = r.data.sizes?.length ? r.data.sizes : SIZES;
        setSize((prev) => (prev && s.includes(prev) ? prev : s.includes("50") ? "50" : s[0]));
      })
      .catch(() => setNotFound(true));
  };

  useEffect(load, [id]);

  if (notFound)
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <p className="font-serif text-3xl text-[#1A1714]">Parfum introuvable</p>
        <Link to="/boutique" className="mt-4 inline-block text-sm font-semibold text-[#C59B27]">Retour à la boutique</Link>
      </div>
    );
  if (!p || !size) return <div className="py-24 text-center text-sm text-[#5C5449]">Chargement…</div>;

  const fav = favorites.includes(p.id);
  const inCompare = compare.includes(p.id);
  const sizes = p.sizes?.length ? p.sizes : SIZES;
  const isPack = sizes.length === 3 && p.prices["30"] === p.prices["50"] && p.prices["50"] === p.prices["100"];
  const isNew = p.created_at && Date.now() - new Date(p.created_at).getTime() < THIRTY_DAYS;

  const submitReview = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/perfumes/${p.id}/reviews`, review);
      toast.success("Merci pour votre avis !");
      setReview({ name: "", rating: 5, comment: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const submitNotify = async (e) => {
    e.preventDefault();
    try {
      await api.post("/notify", { perfume_id: p.id, contact: notifyContact });
      setNotifyDone(true);
      toast.success("Nous vous préviendrons dès son retour !");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-8">
      <div className="grid gap-10 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7 }}
          className="relative overflow-hidden rounded-3xl border border-[#EADFCB] bg-[#F7EEDB]"
        >
          {p.image ? (
            <img src={p.image} alt={p.name} className="aspect-[4/5] w-full object-cover" />
          ) : (
            <div className="flex aspect-[4/5] w-full items-center justify-center bg-gradient-to-br from-[#F7EEDB] to-[#F2A7A0]/30 font-accent text-6xl text-[#C59B27]">
              {p.brand?.[0]}
            </div>
          )}
          <div className="absolute left-4 top-4 flex flex-col gap-1.5">
            {!p.in_stock && (
              <span className="rounded-full bg-[#1A1714] px-4 py-1.5 text-xs font-semibold text-white">
                Rupture de stock
              </span>
            )}
            {isNew && (
              <span data-testid="new-badge" className="rounded-full bg-[#C59B27] px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-white shadow-md shadow-[#C59B27]/40">
                Nouveau
              </span>
            )}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.1 }}>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#C59B27]">{p.brand} · {p.gender}</p>
          <h1 className="mt-2 font-serif text-4xl font-semibold text-[#1A1714]">{p.name}</h1>
          <div className="mt-3 flex items-center gap-2">
            <StarRating value={p.rating || 0} />
            <span className="text-sm text-[#5C5449]">
              {p.rating ? `${p.rating}/5` : "Pas encore noté"} ({p.review_count || 0} avis)
            </span>
          </div>
          <p className="mt-5 leading-relaxed text-[#5C5449]">{p.description}</p>

          <div className="mt-7 space-y-3 rounded-2xl border border-[#EADFCB] bg-white p-5" data-testid="olfactory-pyramid">
            {NOTE_LABELS.map((n) => (
              <div key={n.key} className="flex gap-4">
                <div className="w-28 shrink-0">
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#C59B27]">{n.label}</p>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(p.notes?.[n.key] || []).map((note) => (
                    <span key={note} className="rounded-full bg-[#F7EEDB] px-3 py-1 text-xs font-medium text-[#5C5449]">
                      {note}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {isPack ? (
            <div className="mt-7">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C59B27]">Contenance</p>
              <div data-testid="pack-badge" className="mt-3 rounded-2xl border border-[#C59B27]/40 bg-[#F7EEDB] px-5 py-4">
                <p className="text-sm font-semibold text-[#1A1714]">Coffret duo — 2 flacons de 100 ml</p>
                <p className="mt-1 text-xs text-[#5C5449]">Prix unique pour les deux parfums, livrés ensemble.</p>
              </div>
            </div>
          ) : sizes.length === 1 ? (
            <div className="mt-7">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C59B27]">Contenance</p>
              <div data-testid="single-size-badge" className="mt-3 rounded-2xl border border-[#C59B27]/40 bg-[#F7EEDB] px-5 py-4">
                <p className="text-sm font-semibold text-[#1A1714]">Format unique — {sizes[0]} ml</p>
              </div>
            </div>
          ) : (
            <div className="mt-7">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C59B27]">Contenance</p>
              <div className="mt-3 flex gap-3" data-testid="perfume-size-selector">
                {sizes.map((s) => (
                  <button
                    key={s}
                    data-testid={`detail-size-${s}`}
                    onClick={() => setSize(s)}
                    className={`flex-1 rounded-2xl border px-4 py-3 text-center transition-all active:scale-95 ${
                      size === s ? "border-[#C59B27] bg-[#C59B27] text-white shadow-lg shadow-[#C59B27]/25" : "border-[#EADFCB] bg-white hover:border-[#C59B27]/60"
                    }`}
                  >
                    <span className="block text-sm font-semibold">{s} ml</span>
                    <span className={`block text-xs ${size === s ? "text-white/85" : "text-[#5C5449]"}`}>{formatPrice(p.prices[s])}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-7 flex flex-wrap items-center gap-4">
            <div className="flex items-center rounded-full border border-[#EADFCB] bg-white">
              <button data-testid="qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 text-[#5C5449] hover:text-[#C59B27]"><Minus size={16} /></button>
              <span data-testid="qty-value" className="w-8 text-center text-sm font-semibold">{qty}</span>
              <button data-testid="qty-plus" onClick={() => setQty(Math.min(20, qty + 1))} className="p-3 text-[#5C5449] hover:text-[#C59B27]"><Plus size={16} /></button>
            </div>
            <span data-testid="detail-price" className="font-serif text-3xl font-bold text-[#1A1714]">
              {formatPrice(p.prices[size] * qty)}
            </span>
          </div>

          {p.in_stock ? (
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                data-testid="add-to-cart-button"
                onClick={() => addToCart(p, size, qty)}
                className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#1A1714] px-8 py-4 text-sm font-semibold text-white transition-all hover:bg-[#C59B27] active:scale-95"
              >
                <ShoppingBag size={17} /> Ajouter au panier
              </button>
              <button
                data-testid="detail-favorite-button"
                onClick={() => toggleFavorite(p.id)}
                aria-label="Favori"
                className={`flex h-14 w-14 items-center justify-center rounded-full border transition-all active:scale-90 ${fav ? "border-[#F2A7A0] bg-[#F2A7A0] text-white" : "border-[#EADFCB] bg-white text-[#5C5449] hover:text-[#F2A7A0]"}`}
              >
                <Heart size={19} className={fav ? "fill-white" : ""} />
              </button>
              <button
                data-testid="detail-compare-button"
                onClick={() => toggleCompare(p.id)}
                aria-label="Comparer"
                className={`flex h-14 w-14 items-center justify-center rounded-full border transition-all active:scale-90 ${inCompare ? "border-[#C59B27] bg-[#C59B27] text-white" : "border-[#EADFCB] bg-white text-[#5C5449] hover:text-[#C59B27]"}`}
              >
                <ArrowLeftRight size={19} />
              </button>
            </div>
          ) : (
            <div className="mt-6 rounded-2xl border border-[#EADFCB] bg-white p-5" data-testid="notify-form">
              {notifyDone ? (
                <p className="flex items-center gap-2 text-sm font-semibold text-[#C59B27]">
                  <Check size={16} /> C'est noté ! Nous vous préviendrons dès que {p.name} sera de retour.
                </p>
              ) : (
                <form onSubmit={submitNotify}>
                  <p className="flex items-center gap-2 text-sm font-semibold text-[#1A1714]">
                    <Bell size={16} className="text-[#C59B27]" /> Prévenez-moi quand il revient
                  </p>
                  <div className="mt-3 flex gap-2">
                    <input
                      data-testid="notify-contact-input"
                      value={notifyContact}
                      onChange={(e) => setNotifyContact(e.target.value)}
                      required
                      placeholder="Votre email ou WhatsApp"
                      className="flex-1 rounded-full border border-[#EADFCB] bg-[#FAF8F5] px-4 py-2.5 text-sm outline-none focus:border-[#C59B27]"
                    />
                    <button data-testid="notify-submit-button" className="rounded-full bg-[#C59B27] px-5 py-2.5 text-xs font-semibold text-white transition-all hover:bg-[#A8821B] active:scale-95">
                      Me prévenir
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          <p className="mt-6 text-xs leading-relaxed text-[#5C5449]">
            Paiements acceptés : {PAYMENT_METHODS.map((m) => m.label).join(" • ")}
          </p>
        </motion.div>
      </div>

      <section className="mt-16">
        <Reveal>
          <h2 className="font-serif text-2xl text-[#1A1714] sm:text-3xl">Avis clients</h2>
        </Reveal>
        <div className="mt-8 grid gap-8 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-2" data-testid="reviews-list">
            {(p.reviews || []).length === 0 && (
              <p className="rounded-2xl border border-dashed border-[#EADFCB] p-6 text-sm text-[#5C5449]">
                Soyez le premier à donner votre avis sur ce parfum.
              </p>
            )}
            {(p.reviews || []).map((r) => (
              <Reveal key={r.id} className="rounded-2xl border border-[#EADFCB] bg-white p-5">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-[#1A1714]">{r.name}</p>
                  <StarRating value={r.rating} size={13} />
                </div>
                <p className="mt-2 text-sm leading-relaxed text-[#5C5449]">{r.comment}</p>
                <p className="mt-2 text-xs text-[#5C5449]/60">{new Date(r.created_at).toLocaleDateString("fr-FR")}</p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <form onSubmit={submitReview} className="rounded-2xl border border-[#EADFCB] bg-white p-6" data-testid="review-form">
              <p className="font-serif text-lg font-semibold text-[#1A1714]">Donner mon avis</p>
              <input
                data-testid="review-name-input"
                value={review.name}
                onChange={(e) => setReview({ ...review, name: e.target.value })}
                required
                placeholder="Votre prénom"
                className="mt-4 w-full rounded-xl border border-[#EADFCB] bg-[#FAF8F5] px-4 py-2.5 text-sm outline-none focus:border-[#C59B27]"
              />
              <div className="mt-3">
                <StarRating value={review.rating} size={22} onChange={(v) => setReview({ ...review, rating: v })} />
              </div>
              <textarea
                data-testid="review-comment-input"
                value={review.comment}
                onChange={(e) => setReview({ ...review, comment: e.target.value })}
                required
                rows={3}
                placeholder="Votre avis sur ce parfum…"
                className="mt-3 w-full rounded-xl border border-[#EADFCB] bg-[#FAF8F5] px-4 py-2.5 text-sm outline-none focus:border-[#C59B27]"
              />
              <button data-testid="review-submit-button" className="mt-4 w-full rounded-full bg-[#1A1714] py-3 text-sm font-semibold text-white transition-all hover:bg-[#C59B27] active:scale-95">
                Publier mon avis
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;
